import React, { Fragment, useEffect, useState } from "react";
import { Map, Marker, Overlay } from "pigeon-maps";
import CircularProgress from "@mui/material/CircularProgress";
import { Link } from "react-router-dom";
import { MainPageHero } from "./components/MainPageHero";
import { CollaboratorService } from "../../services/collaborator.service";
import { FeatureCard } from "./components/FeatureCard";
import { MainPagesSectionHeader } from "./components/MainPagesSectionHeader";
import { transformCloudinaryUrlTo4x3 } from "../../shared/helpers";

interface WebCollaborator {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  imgUrl?: string;
  images?: Array<{ url: string; isMain: boolean }>;
  sortingOrder?: number;
}

// Map center and branch locations
const mapCenter: [number, number] = [21.019557, -89.613934];
const branches: { position: [number, number]; name: string }[] = [
  { position: [21.019557, -89.595265], name: "Sucursal Urban" },
  { position: [21.002827, -89.614862], name: "Sucursal Montejo" },
  { position: [21.046215, -89.630306], name: "Sucursal The Harbor" },
];

const collaboratorService = new CollaboratorService();

export const MainPageIndex = () => {
  const [collaborators, setCollaborators] = useState<WebCollaborator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortedCollaborators, setsortedCollaborators] = useState<
    WebCollaborator[]
  >([]);
  useEffect(() => {
    const loadCollaborators = async () => {
      setIsLoading(true);
      try {
        const data =
          (await collaboratorService.getCollaboratorsForWeb()) as unknown as WebCollaborator[];
        setCollaborators(data);
      } catch (error) {
        console.error("Error loading collaborators:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCollaborators();
  }, []);

  useEffect(() => {
    if (collaborators.length > 0) {
      const sortedCollaborators = collaborators.sort(
        (a, b) => (a?.sortingOrder ?? Infinity) - (b?.sortingOrder ?? Infinity)
      );
      setsortedCollaborators(sortedCollaborators);
    }
  }, [collaborators]);

  return (
    <Fragment>
      <MainPageHero />
      {/* HERO */}

      <section className="sectionFeatures u-bgPrimaryLightest u-pb-6r">
        <div className="container">
          <MainPagesSectionHeader title="Lo que nos distingue" />
          <div className="featuresContainer">
            <FeatureCard
              imgSrc="assets/imgs/mainPage/char-personal.png"
              title="Personalizado"
              text="El seguimiento que damos a cada uno de nuestros pacientes es personalizado y acorde con sus necesidades."
            />
            <FeatureCard
              imgSrc="assets/imgs/mainPage/char-experience.png"
              title="Experiencia"
              text="Tenemos 30 años de experiencia en veterinaria, por lo que
                  somos un referente en Mérida y en todo el sureste."
            />
            <FeatureCard
              imgSrc="assets/imgs/mainPage/char-specialist.png"
              title="Especialidad"
              text="Nuestros médicos están especializados en oftalmología y
              prestamos servicios relacionados en otras especialidades."
            />
            <FeatureCard
              imgSrc="assets/imgs/mainPage/char-coverage.png"
              title="Cobertura"
              text="Contamos con tres sucursales, todas en las tiendas Petco en la
              ciudad de Mérida."
            />
          </div>
        </div>
      </section>

      <section className="u-bgPrimaryLighter u-pb-6r">
        <div className="container">
          <MainPagesSectionHeader title="Promociones" />
          <div
            id="carouselExampleIndicators"
            className="carousel slide"
            data-bs-ride="carousel"
          >
            <ol className="carousel-indicators">
              <li
                data-bs-target="#carouselExampleIndicators"
                data-bs-slide-to="0"
                className="active"
              ></li>
              <li
                data-bs-target="#carouselExampleIndicators"
                data-bs-slide-to="1"
              ></li>
              <li
                data-bs-target="#carouselExampleIndicators"
                data-bs-slide-to="2"
              ></li>
            </ol>
            <div className="carousel-inner">
              <div className="carousel-item active">
                <img
                  src="assets/imgs/mainPage/promo/promo1.jpg"
                  className="d-block"
                  alt="..."
                />
              </div>
              <div className="carousel-item">
                <img
                  src="assets/imgs/mainPage/promo/promo2.jpg"
                  className="d-block"
                  alt="..."
                />
              </div>
              <div className="carousel-item">
                <img
                  src="assets/imgs/mainPage/promo/promo3.jpg"
                  className="d-block"
                  alt="..."
                />
              </div>
            </div>
            <a
              className="carousel-control-prev"
              href="#carouselExampleIndicators"
              role="button"
              data-bs-slide="prev"
            >
              <span
                className="carousel-control-prev-icon"
                aria-hidden="true"
              ></span>
              <span className="visually-hidden">Previous</span>
            </a>
            <a
              className="carousel-control-next"
              href="#carouselExampleIndicators"
              role="button"
              data-bs-slide="next"
            >
              <span
                className="carousel-control-next-icon"
                aria-hidden="true"
              ></span>
              <span className="visually-hidden">Next</span>
            </a>
          </div>
        </div>
      </section>

      <section className="u-bgPrimaryLightest u-pb-6r">
        <div className="container">
          <MainPagesSectionHeader title="Quiénes somos" />

          <div className="collaboratorCardsWrapper">
            {isLoading ? (
              <CircularProgress />
            ) : (
              sortedCollaborators.map((collaborator) => {
                const imgUrl =
                  Array.isArray(collaborator.images) &&
                  collaborator.images.length > 0
                    ? collaborator.images.find((image) => image.isMain)?.url ??
                      collaborator.images[0].url
                    : collaborator.imgUrl;

                const transformedImgUrl = transformCloudinaryUrlTo4x3(
                  imgUrl ?? ""
                );

                return (
                  <div className="collaboratorCard" key={collaborator.id}>
                    <img
                      className="collaboratorCard_img"
                      src={transformedImgUrl}
                      alt="colaborador"
                    />
                    <div className="coll-name">
                      <p className="collaboratorCard_text">
                        {`${collaborator.first_name} ${collaborator.last_name} `}
                        <span className="collaboratorCard_position">
                          {collaborator.position}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      <section className="u-bgPrimaryLighter u-pb-6r">
        <div className="container">
          <MainPagesSectionHeader title="Servicios" />

          <div className="serviceImgCardWrapper">
            <div className="serviceImgCard">
              <img src="assets/imgs/mainPage/services/consulta.jpg" alt="" />
              <p className="serviceImgCard_text">Consultas</p>
            </div>
            <div className="serviceImgCard">
              <img src="assets/imgs/mainPage/services/vaccine.jpg" alt="" />
              <p className="serviceImgCard_text">Vacunas</p>
            </div>
            <div className="serviceImgCard">
              <img
                className="serviceImgCard_img"
                src="assets/imgs/mainPage/services/desp.jpg"
                alt=""
              />
              <p className="serviceImgCard_text">Desparasitaciones</p>
            </div>
            <div className="serviceImgCard">
              <img src="assets/imgs/mainPage/services/lab.jpg" alt="" />
              <p className="serviceImgCard_text">Laboratorio</p>
            </div>
            <div className="serviceImgCard">
              <img src="assets/imgs/mainPage/services/surgery.jpg" alt="" />
              <p className="serviceImgCard_text">Cirugía</p>
            </div>
            <div className="serviceImgCard">
              <img src="assets/imgs/mainPage/services/pedig.jpg" alt="" />
              <p className="serviceImgCard_text">Pedigrí</p>
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <Link className="" to="/services">
              <button type="button" className="c-button -large">
                Conozca más
              </button>
            </Link>
          </div>
        </div>
      </section>

      <section className="u-bgPrimaryLightest u-pb-6r">
        <div className="container">
          <MainPagesSectionHeader title="Contacto" />
          <div>
            {/* <MapContainer center={position} zoom={13} scrollWheelZoom={false}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={position}>
              <Popup>
                A pretty CSS3 popup. <br /> Easily customizable.
              </Popup>
            </Marker>
          </MapContainer> */}
            {/* pigeon-maps: React 19 compatible, no API key needed */}
            <div className="mb-5">
              <Map
                height={400}
                center={mapCenter}
                zoom={12}
                attribution={false}
              >
                {branches.map((branch, index) => (
                  <Marker
                    key={index}
                    anchor={branch.position}
                    color="#1976d2"
                    onClick={() => {}}
                  />
                ))}
                {branches.map((branch, index) => (
                  <Overlay
                    key={`overlay-${index}`}
                    anchor={branch.position}
                    offset={[0, 50]}
                  >
                    <div
                      style={{
                        background: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        whiteSpace: "nowrap",
                        transform: "translateX(-50%)",
                      }}
                    >
                      {branch.name}
                    </div>
                  </Overlay>
                ))}
              </Map>
            </div>
          </div>
          <div className="branchesWrapper">
            <div className="branchCard c-card u-bgPrimaryLighter u-textWhite">
              <p className="branchCard c-card_title">Urban</p>
              <p>
                <i className="branchCard_icon fas fa-phone"></i> 999 406 9174
              </p>
              <p>
                {" "}
                <i className="branchCard_icon fab fa-whatsapp-square"></i> 999
                184 1434
              </p>
              <p>
                <i className="branchCard_icon fas fa-thumbtack"></i> Av. Cámara
                de Comercio 215, Monterreal
              </p>
            </div>
            <div className="branchCard c-card u-bgPrimaryLighter u-textWhite">
              <p className="branchCard c-card_title">Harbor</p>
              <p>
                <i className="branchCard_icon fas fa-phone"></i> 999 925 0015
              </p>
              <p>
                <i className="branchCard_icon fas fa-thumbtack"></i> Calle 60
                261, Zona Industrial
              </p>
            </div>
            <div className="branchCard c-card u-bgPrimaryLighter u-textWhite">
              <p className="branchCard c-card_title">Montejo</p>
              <p>
                <i className="branchCard_icon fas fa-phone"></i> 999 400 7974
              </p>
              <p>
                <i className="branchCard_icon fas fa-thumbtack"></i> Prol. Paseo
                Montejo 99, Campestre
              </p>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};
